/**
 * What `codec: 'auto'` means. The browser's default order puts VP8 first, which
 * is a software encoder everywhere; at 1080p30 that is the first stage to drop
 * frames under CPU load. Where the platform has a hardware encoder for the
 * program's size and rate, that codec is preferred instead.
 */
import type { VideoCodecPreference } from '../core/model';

export type ConcreteCodec = Exclude<VideoCodecPreference, 'auto'>;

export interface EncodeTarget {
  width: number;
  height: number;
  framerate: number;
  /** bits per second */
  bitrate: number;
}

/** One entry of RTCRtpSender.getCapabilities('video').codecs, as far as it matters here. */
export interface CodecFormat {
  mimeType: string;
  sdpFmtpLine?: string;
}

export interface EncodeAnswer {
  codec: ConcreteCodec;
  /** Identifies the exact format (see `formatKey`). */
  format: string;
  supported: boolean;
  powerEfficient: boolean;
}

/** `navigator.mediaCapabilities.encodingInfo`, narrowed to what is used. */
export type EncodingInfo = (configuration: {
  type: 'webrtc';
  video: { contentType: string; width: number; height: number; bitrate: number; framerate: number };
}) => Promise<{ supported: boolean; powerEfficient: boolean }>;

/**
 * Asked in this order, and the first family with a power-efficient format wins.
 * H.264 leads because every browser decodes it, usually in hardware too; VP9 and
 * AV1 follow for platforms whose hardware encoder is one of those.
 */
const FAMILIES: ReadonlyArray<[ConcreteCodec, string]> = [
  ['h264', 'video/h264'],
  ['vp9', 'video/vp9'],
  ['av1', 'video/av1'],
];

/**
 * A codec is not one thing to the encoder: Chrome on a Mac hardware-encodes H.264
 * baseline and main with packetization-mode 1, and software-encodes constrained
 * baseline and everything in packetization-mode 0. Capability answers are therefore
 * taken, and remembered, per exact format.
 */
export function formatKey(format: CodecFormat): string {
  return `${format.mimeType.toLowerCase()};${format.sdpFmtpLine ?? ''}`;
}

function contentTypeOf(format: CodecFormat): string {
  return format.sdpFmtpLine ? `${format.mimeType};${format.sdpFmtpLine}` : format.mimeType;
}

export function pickPowerEfficient(answers: readonly EncodeAnswer[]): ConcreteCodec | null {
  for (const [codec] of FAMILIES) {
    if (answers.some((a) => a.codec === codec && a.supported && a.powerEfficient)) return codec;
  }
  return null;
}

export async function probeEncoders(
  target: EncodeTarget,
  encodingInfo: EncodingInfo,
  formats: readonly CodecFormat[],
): Promise<EncodeAnswer[]> {
  const wanted: Array<{ codec: ConcreteCodec; format: CodecFormat }> = [];
  for (const [codec, mime] of FAMILIES) {
    for (const format of formats) {
      if (format.mimeType.toLowerCase() === mime) wanted.push({ codec, format });
    }
  }
  return Promise.all(
    wanted.map(async ({ codec, format }): Promise<EncodeAnswer> => {
      try {
        const info = await encodingInfo({ type: 'webrtc', video: { contentType: contentTypeOf(format), ...target } });
        return { codec, format: formatKey(format), supported: info.supported === true, powerEfficient: info.powerEfficient === true };
      } catch {
        // A format the browser does not accept as a WebRTC content type rejects the query.
        return { codec, format: formatKey(format), supported: false, powerEfficient: false };
      }
    }),
  );
}

export interface AutoCodecDecision {
  /** The codec family to put first, or 'auto' to leave the browser's order alone. */
  codec: VideoCodecPreference;
  /** Keys of the formats of that family with a power-efficient encoder; they go first of all. */
  efficientFormats: ReadonlySet<string>;
  reason: string;
}

const NONE: ReadonlySet<string> = new Set();

/**
 * Used when a chosen hardware codec turned out not to encode. VP8 is named
 * explicitly because clearing the preference does not do it: measured in Chrome,
 * a re-offer with an empty preference list keeps the negotiated codec first.
 */
export const SOFTWARE_FALLBACK: AutoCodecDecision = { codec: 'vp8', efficientFormats: NONE, reason: 'software VP8' };

export const BROWSER_DEFAULT: AutoCodecDecision = { codec: 'auto', efficientFormats: NONE, reason: 'browser default order kept' };

export async function decideAutoCodec(
  target: EncodeTarget,
  encodingInfo: EncodingInfo | null,
  formats: readonly CodecFormat[],
): Promise<AutoCodecDecision> {
  if (!encodingInfo) return { ...BROWSER_DEFAULT, reason: 'no MediaCapabilities API; browser default order kept' };
  const answers = await probeEncoders(target, encodingInfo, formats);
  const size = `${target.width}x${target.height}@${Math.round(target.framerate)}`;
  const choice = pickPowerEfficient(answers);
  const summary = FAMILIES.map(([codec]) => {
    const own = answers.filter((a) => a.codec === codec && a.supported);
    if (own.length === 0) return `${codec}:unsupported`;
    const efficient = own.filter((a) => a.powerEfficient).length;
    return `${codec}:${efficient > 0 ? `hardware(${efficient}/${own.length} formats)` : 'software'}`;
  }).join(' ');
  if (!choice) {
    return { ...BROWSER_DEFAULT, reason: `no power-efficient encoder for ${size} (${summary}); browser default order kept` };
  }
  return {
    codec: choice,
    efficientFormats: new Set(answers.filter((a) => a.codec === choice && a.supported && a.powerEfficient).map((a) => a.format)),
    reason: `${choice} has a power-efficient encoder for ${size} (${summary})`,
  };
}

/** The size and rate the program is actually produced at, with broadcast defaults for unknowns. */
export function encodeTargetOf(stream: MediaStream, videoKbps: number): EncodeTarget {
  const settings = stream.getVideoTracks()[0]?.getSettings?.() ?? {};
  return {
    width: settings.width && settings.width > 0 ? settings.width : 1280,
    height: settings.height && settings.height > 0 ? settings.height : 720,
    framerate: settings.frameRate && settings.frameRate > 0 ? settings.frameRate : 30,
    bitrate: Math.max(100_000, videoKbps * 1000),
  };
}

export function browserEncodingInfo(): EncodingInfo | null {
  const capabilities = typeof navigator !== 'undefined' ? navigator.mediaCapabilities : undefined;
  if (!capabilities || typeof capabilities.encodingInfo !== 'function') return null;
  return (configuration) => capabilities.encodingInfo(configuration as MediaEncodingConfiguration);
}

/** The formats this browser can send, i.e. the ones worth asking about. */
export function browserSendFormats(): CodecFormat[] {
  if (typeof RTCRtpSender === 'undefined' || typeof RTCRtpSender.getCapabilities !== 'function') return [];
  return RTCRtpSender.getCapabilities('video')?.codecs ?? [];
}

/**
 * The first capability query of a page can take seconds while the browser brings
 * up its encoder factories. An offer waits this long for the answer and otherwise
 * goes out in the browser's default order; later offers use the answer once it is in.
 */
export const AUTO_CODEC_WAIT_MS = 2000;

export async function settledWithin<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const result = await Promise.race([
    promise,
    new Promise<T>((resolve) => {
      timer = setTimeout(() => resolve(fallback), ms);
    }),
  ]);
  clearTimeout(timer);
  return result;
}
